import type { ReactNode } from 'react';
import { cx } from './cx.js';
import { QuietGround } from './QuietGround.js';

/**
 * The pool of light a menu screen stands in.
 *
 * Fifteen screens opened with the same eight classes: a column, capped at the
 * width a line of text stays readable at, centered in the room, and padded off
 * every edge by the notch and the home indicator. It was copied fifteen times
 * because it had no name, and the copies had already begun to differ — one had
 * lost `mx-auto`, one had grown a `justify-center-safe` that belongs to the
 * screen and not to the shell.
 *
 * So the shell is a component and the screen passes what is genuinely its own.
 * The safe-area padding is the reason this matters most: it is four `calc()`
 * expressions that have to agree, and a screen that gets one of them wrong
 * loses its bottom row under the home indicator on one phone and nowhere else.
 *
 * The geometry itself is the `screen` utility in `styles.css`. The column width
 * is the one thing a screen genuinely chooses between, so it is a prop: the
 * home screen used to pass `max-w-[26.5rem]` and beat the utility only by
 * emission order, which is not a contract anybody should rely on.
 */

export interface ScreenProps {
  /**
   * Vertically center the column instead of stacking from the top. For a screen
   * that is one short block of content, where a top-aligned column reads as a
   * page that failed to load the rest of itself.
   */
  readonly centered?: boolean | undefined;
  /**
   * How wide the column is allowed to get. `readable` is a text column, and is
   * what almost every screen wants. `menu` is a stack of tiles, which stays a
   * stack instead of spreading into a row of stamps on a wide phone.
   * `split` is a text column on a phone that opens to two columns from the
   * tablet breakpoint: Settings, with its groups in a sidebar beside the one
   * that is open. `wide` is a laptop page with columns side by side: the
   * Learn reference pages set a rail or a panel beside their text, and Stats
   * sets its hands beside the record.
   *
   * `full` is a Lobby room whose header runs edge to edge (Back on the left,
   * the online count on the right) and whose content sets its own column.
   *
   * `poster` is the home: the menu column on a phone, and the whole screen
   * from the `tablet` breakpoint up, where the lockup is the widest thing and
   * sets the column's width itself.
   */
  readonly width?: 'readable' | 'menu' | 'split' | 'wide' | 'full' | 'poster' | undefined;
  /**
   * The ground motif, two paper suits, behind the column (BRAND § The ground
   * motif), read at the `tablet` breakpoint for its composition. `suits`
   * stays put while the page scrolls, as the room does: a Learn page scrolls
   * far. `suits-scroll` scrolls with a page about one screen tall, the private
   * table and its invitation. Play, Settings and the home poster still draw
   * that second placement by hand, in an `isolate` wrapper around the screen,
   * and move onto it when next touched with a browser pass.
   */
  readonly ground?: 'suits' | 'suits-scroll' | undefined;
  /** Layout and state only. Never the shell's own geometry. */
  readonly className?: string | undefined;
  readonly children: ReactNode;
}

const WIDTH = {
  readable: 'max-w-readable',
  menu: 'max-w-menu',
  split: 'max-w-readable tablet:max-w-230',
  wide: 'max-w-6xl',
  full: 'w-full',
  poster: 'max-w-menu tablet:max-w-none',
} satisfies Record<NonNullable<ScreenProps['width']>, string>;

export function Screen({
  centered = false,
  width = 'readable',
  ground,
  className,
  children,
}: ScreenProps): React.JSX.Element {
  return (
    <main
      className={cx(
        'screen',
        WIDTH[width],
        centered && 'justify-center-safe',
        ground !== undefined && 'isolate',
        ground === 'suits-scroll' && 'relative',
        className,
      )}
    >
      {/* Behind the column without wrapping it, the way `Cover` does it:
          `isolate` makes the column its own stacking context, so a negative
          index puts the ground above the document's `--ground` and under every
          child. Fixed, because the room stays put while the page scrolls, and
          out of flow, so the column's gap never counts it. */}
      {ground === 'suits' && (
        <div className="pointer-events-none fixed inset-0 -z-10 print:hidden">
          <QuietGround />
        </div>
      )}
      {/* The same pair, scrolling: the field fills the column's own box, which
          a full-width screen makes the page. */}
      {ground === 'suits-scroll' && <QuietGround className="-z-10" />}
      {children}
    </main>
  );
}
